import React from 'react';
import { ArrowRight, Calendar } from 'lucide-react';
import { heroData } from '../data/mockData';
import vanceVideo from '../media/presentacion-vance-avatar-evolvance.mp4';

const Hero = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* HERO — fondo teal oscuro */}
      <section
        id="hero"
        className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      >
        {/* Background Video with Overlay */}
        <div className="absolute inset-0 z-0 bg-black">
          {/* Video anchor al lado derecho */}
          <video
            src={vanceVideo}
            autoPlay
            loop
            muted
            playsInline
            className="absolute top-0 right-0 h-full opacity-85"
            style={{ width: '75%', objectFit: 'cover', objectPosition: 'left center' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/10 to-black/50"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 px-6 md:px-16 pt-36 pb-20">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-7 animate-fade-in-up">
            <div className="w-10 h-[1px] bg-[#D4AF37]"></div>
            <span className="font-outfit text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-medium">
              Transformación Digital 360º · Sevilla
            </span>
          </div>

          {/* Title */}
          <h1
            className="font-outfit font-black leading-[0.95] tracking-tight max-w-[900px] mb-5 animate-fade-in-up"
            style={{
              fontSize: 'clamp(3.2rem, 7vw, 6.5rem)',
              animationDelay: '0.1s'
            }}
          >
            <span className="sr-only">Transformación Digital 360 y Desarrollo de Software a Medida en Sevilla, Málaga y toda Andalucía - </span>
            Para de competir.
            <span className="block text-[#D4AF37] italic">Empieza a dominar.</span>
          </h1>

          {/* Subtitle */}
          <div className="max-w-[520px] mb-12 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: 'rgba(245,242,236,0.75)' }}>
              No somos una agencia más. <strong className="text-white font-medium">Somos el equipo de élite que entra en tu empresa</strong>, identifica cada punto de fuga y lo convierte en palanca de crecimiento. Software, automatizaciones y marketing unificados bajo un solo objetivo: <strong className="text-white font-medium">tus resultados.</strong>
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <button
              onClick={() => scrollToSection('#contacto')}
              className="inline-flex items-center gap-2.5 bg-[#D4AF37] text-[#080808] font-outfit font-bold text-sm uppercase tracking-wider px-9 py-4 transition-all duration-300 hover:bg-[#E8C97A] hover:translate-y-[-2px] hover:shadow-[0_12px_40px_rgba(201,168,76,0.3)]"
              style={{ clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)' }}
            >
              Quiero resultados ahora
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('#servicios')}
              className="inline-flex items-center gap-2.5 text-white text-sm tracking-wider py-4 px-5 border-b border-white/25 transition-all duration-300 hover:text-[#D4AF37] hover:border-[#D4AF37]"
            >
              Ver cómo lo hacemos
            </button>
          </div>

          {/* Calendly Badge */}
          <a
            href="https://calendly.com/admin-evol-vance/evolweb"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-8 px-5 py-2.5 text-xs uppercase tracking-wider text-[#D4AF37] border border-[#D4AF37]/35 bg-[#D4AF37]/10 transition-all duration-300 hover:bg-[#D4AF37]/20 animate-fade-in-up w-fit"
            style={{ animationDelay: '0.4s' }}
          >
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0"></div>
            <Calendar className="w-3.5 h-3.5" />
            Agenda una llamada gratuita de 30 min — sin compromiso
          </a>
        </div>

        {/* Stats */}
        <div className="hidden md:flex absolute bottom-12 right-16 gap-12 z-10 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
          {[
            { value: '360°', label: 'Solución completa' },
            { value: '3x', label: 'ROI medio cliente' },
            { value: '0€', label: 'Si no hay resultados' }
          ].map((stat, index) => (
            <div key={index} className="text-right">
              <div className="font-outfit font-black text-3xl text-[#D4AF37] leading-none">{stat.value}</div>
              <div className="text-[0.72rem] uppercase tracking-wider mt-1" style={{ color: 'rgba(245,242,236,0.5)' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Mobile Stats */}
        <div className="flex md:hidden flex-wrap gap-8 px-6 pb-10 relative z-10 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
          {[
            { value: '360°', label: 'Solución completa' },
            { value: '3x', label: 'ROI medio cliente' },
            { value: '0€', label: 'Si no hay resultados' }
          ].map((stat, index) => (
            <div key={index}>
              <div className="font-outfit font-black text-2xl text-[#D4AF37] leading-none">{stat.value}</div>
              <div className="text-[0.72rem] uppercase tracking-wider mt-1" style={{ color: 'rgba(245,242,236,0.5)' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Scroll Line */}
        <div className="hidden md:flex absolute bottom-8 left-16 items-center gap-3.5 z-10 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
          <div className="w-[1px] h-10" style={{ background: 'linear-gradient(to bottom, transparent, rgba(245,242,236,0.4))' }}></div>
          <span className="text-[0.72rem] uppercase tracking-[0.15em]" style={{ color: 'rgba(245,242,236,0.4)' }}>Desplázate</span>
        </div>
      </section>

      {/* BANDA DE CAPTACIÓN */}
      <div className="bg-[#D4AF37] py-5 px-6 md:px-16 flex flex-col md:flex-row items-center justify-between gap-4 flex-wrap">
        <div>
          <span className="font-outfit font-extrabold text-sm uppercase tracking-wider text-[#080808]">
            ¿Tu empresa está estancada?
          </span>
          <span className="text-sm text-[#080808]/70 ml-2 hidden md:inline">
            En 30 minutos te decimos exactamente qué está fallando — y cómo lo arreglamos.
          </span>
        </div>
        <a
          href="https://calendly.com/admin-evol-vance/evolweb"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#080808] text-[#D4AF37] font-outfit font-bold text-xs uppercase tracking-wider px-7 py-3 transition-all duration-300 hover:bg-[#111] hover:translate-y-[-1px] whitespace-nowrap rounded-sm"
        >
          Reserva tu diagnóstico gratuito
          <ArrowRight className="w-3.5 h-3.5" />
        </a>
      </div>

    </>
  );
};

export default Hero;
